<template>
  <div class="route-selection route-none">
    <div class="main-container">
      <!-- パンくず -->
      <div aria-label="Breadcrumb" class="breadcrumb-container">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <RouterLink to="/settings" custom v-slot="{ href }">
              <a
                :href="href"
                @touchend.prevent.stop="(e) => handleTouchNavigate(goToSettings, e)"
                @click.prevent="(e) => handleClickNavigate(goToSettings, e)"
              >
                設定トップ
              </a>
            </RouterLink>
            <span class="breadcrumb-separator"></span>
          </li>
          <li class="breadcrumb-item">
            <span aria-current="page">航路選択</span>
          </li>
        </ol>
      </div>

      <div class="container">
        <h2 class="config-headline">
          <img
            src="../assets/images/icon/icon_route_orange.svg"
            alt=""
            class="icon-config-headline"
          />
          航路選択
        </h2>
        <div class="config-attention route-attention">
          航路を登録する場合は、パソコン上でCSVファイルを取り込んでください。<br />スマートフォンからは、登録済みの航路の確認と編集ができます。
        </div>
        <div class="config-content">
          <div class="route-list-box">
            <div class="route-list-area">
              <label for="" class="label-l"> 飛行エリア </label>
              <select
                v-model="selectedAreaId"
                name="area"
                :disabled="isLoadingAreas || areas.length === 0 || !!noAreaError"
                style="font-size: 16px; color: var(--font-color)"
              >
                <option v-for="a in areas" :key="a.id" :value="a.id">
                  {{ a.name }}
                </option>
              </select>
            </div>
            <div v-if="currentRoutes.length === 0" class="route-none-attention">
              航路が登録されていません
            </div>
            <ul v-else class="route-list">
              <li v-for="r in currentRoutes" :key="r.id">
                <button
                  class="route-button"
                  type="button"
                  @touchend.prevent.stop="(e) => handleTouchNavigate(() => editRoute(r.id), e)"
                  @click.prevent="(e) => handleClickNavigate(() => editRoute(r.id), e)"
                >
                  <div>
                    <p class="route-button-name" :title="r.name">{{ r.name }}</p>
                    <div>
                      <div class="sg">発：{{ r.from }}</div>
                      <div class="sg">着：{{ r.to }}</div>
                    </div>
                  </div>
                  <img src="../assets/images/icon/icon_edit_pen.svg" alt="" />
                </button>
              </li>
            </ul>
          </div>
          <div class="attention-sp">※航路は最大{{ MAX_ROUTES }}つまで設定可能です。</div>
          <div class="export-action">
            <div class="import-route">
              <p class="import">
                航路情報をCSVから読み込む
                <span>※航路は最大{{ MAX_ROUTES }}つまで設定可能です。</span>
              </p>
              <button
                class="button-primary"
                type="button"
                @click="openCsvPicker"
                :disabled="isFileDisabled || isSaving"
                :class="{ 'is-disabled': isFileDisabled || isSaving }"
                style="font-size: 16px"
              >
                ファイルを選択
              </button>

              <input
                ref="fileInputRef"
                style="display: none"
                type="file"
                accept=".csv,text/csv"
                @change="onCsvChange"
                :disabled="isFileDisabled"
              />
              <ErrorMessageBox v-if="noAreaError" :message="noAreaError" />
              <ErrorMessageBox v-if="maxCsvError" :message="maxCsvError" />

              <button class="link" type="button" @click="downloadCsvFormat">
                <img src="../assets/images/icon/icon_download.svg" alt="" class="icon-edit" />
                フォーマット（CSV）をダウンロード
              </button>
            </div>
            <div class="import-alert">
              <div
                :class="[
                  'import-alert-title',
                  csvErrors.length ? 'import-alert-title-error' : 'import-alert-title-warning',
                ]"
              >
                <img src="../assets/images/icon/icon_error_white.svg" alt="" />
                <p v-if="csvErrors.length">
                  {{ getFieldMessage('loadfailed904_1_1') }}
                  <span> {{ getFieldMessage('loadfailed904_1_2') }} </span>
                </p>
                <p v-else>
                  {{ getFieldMessage('loadfailed904_1_3') }}
                  <span> {{ getFieldMessage('loadfailed904_1_4') }} </span>
                </p>
              </div>
              <div
                :class="[
                  'import-alert-p',
                  csvErrors.length ? 'import-alert-p-error' : 'import-alert-p-warning',
                ]"
              >
                <ul v-html="getFieldMessage('loadfailed904_2_1')"></ul>
                <p v-if="csvErrors.length" v-html="getFieldMessage('loadfailed904_2_2')"></p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Toast />

      <!-- パンくず -->
      <div aria-label="Breadcrumb" class="breadcrumb-container-under">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <RouterLink to="/settings" custom v-slot="{ href }">
              <a
                :href="href"
                @touchend.prevent.stop="(e) => handleTouchNavigate(goToSettings, e)"
                @click.prevent="(e) => handleClickNavigate(goToSettings, e)"
              >
                設定トップ
              </a>
            </RouterLink>
            <span class="breadcrumb-separator"></span>
          </li>
          <li class="breadcrumb-item">
            <span aria-current="page">航路選択</span>
          </li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import Toast from '@/components/common/Toast.vue'
import { fetchFlightAreas as fetchFlightAreasApi } from '@/api/flightAreasApi'
import {
  fetchFlightRoutes as fetchFlightRoutesApi,
  registerFlightRoutesByCsv,
} from '@/api/flightRoutesApi'
import type { FlightRoute } from '@/api/flightRoutesApi'
import { getFieldMessage } from '@/utils/message'
import ErrorMessageBox from '@/components/form/ErrorMessageBox.vue'
import { useToast } from '@/composables/useToast'

/** =========================
 * 型（UI用）
 ========================= */
type UiFlightArea = { id: number; name: string }

type RouteItem = {
  id: number
  name: string
  from: string
  to: string
  areaId: number
  areaName: string
}

type FlightAreaApi = {
  flight_area_id: number
  flight_area_name: string
}

/** =========================
 * 定数
 ========================= */
const router = useRouter()
const MAX_ROUTES = 5
let lastTouchNavigateAt = 0

/** =========================
 * state
 ========================= */
const areas = ref<UiFlightArea[]>([])
const selectedAreaId = ref<number | null>(null)
const isLoadingAreas = ref(false)
const isLoadingRoutes = ref(false)
const isSaving = ref(false)
const routeErrors = ref<string[]>([])
const apiRoutes = ref<RouteItem[]>([])
const csvErrors = ref<string[]>([])
const maxCsvError = ref<string | undefined>(undefined)
const noAreaError = ref<string | undefined>(undefined)
const fileInputRef = ref<HTMLInputElement | null>(null)
const { show } = useToast()

/** =========================
 * computed
 ========================= */
const currentRoutes = computed<RouteItem[]>(() => {
  if (selectedAreaId.value == null) return []
  return apiRoutes.value.filter((r) => r.areaId === selectedAreaId.value).slice(0, MAX_ROUTES)
})
const isFileDisabled = computed(() => currentRoutes.value.length >= MAX_ROUTES)

/** =========================
 * 変換
 ========================= */
function toRouteItem(r: FlightRoute): RouteItem {
  const from = r.points?.[0]?.point_name ?? ''
  const to = r.points?.[r.points.length - 1]?.point_name ?? ''
  return {
    id: r.flight_route_id,
    areaId: r.flight_area_id,
    areaName: r.flight_area_name,
    name: r.flight_route_name,
    from,
    to,
  }
}

/** =========================
 * 飛行エリア取得（flightAreasApi.ts 使用）
 ========================= */
async function fetchAreasForSelect() {
  isLoadingAreas.value = true
  csvErrors.value = []

  try {
    const list = await fetchFlightAreasApi() // FlightArea[] が返る想定

    if (!list || list.length === 0) {
      // エリアが0件の場合
      areas.value = []
      selectedAreaId.value = null
      //飛行エリア設定確認画面に遷移
      router.replace('/flight-area-empty')
      return
    }

    areas.value = list.map((a: FlightAreaApi) => ({
      id: a.flight_area_id,
      name: a.flight_area_name,
    }))

    const firstArea = areas.value[0]
    if (firstArea) {
      selectedAreaId.value = firstArea.id
    }
  } finally {
    isLoadingAreas.value = false
  }
}

/** =========================
 * 航路取得（flightRoutesApi.ts 使用）
 ========================= */
async function fetchAndSetRoutes(flightAreaId: number) {
  isLoadingRoutes.value = true
  routeErrors.value = []

  // 念のためここでもクリア
  apiRoutes.value = []

  try {
    const routes = await fetchFlightRoutesApi(flightAreaId)
    const list = (routes ?? []).filter((r) => r.flight_area_id === flightAreaId)

    if (list.length === 0) {
      //routeErrors.value = ['航路データが存在しません。']
      return
    }

    apiRoutes.value = list.map(toRouteItem)
  } finally {
    isLoadingRoutes.value = false
  }
}

/** =========================
 * 初期化・エリア変更時
 ========================= */
onMounted(async () => {
  await fetchAreasForSelect()
})

watch(selectedAreaId, async (id) => {
  // まず必ずクリア
  apiRoutes.value = []
  routeErrors.value = []

  // CSV関連もリセット
  csvErrors.value = []
  //エラーをクリア
  noAreaError.value = undefined
  maxCsvError.value = undefined
  if (fileInputRef.value) fileInputRef.value.value = ''

  if (id == null) {
    return
  }

  await fetchAndSetRoutes(id)
})

/** =========================
 * CSV：読み込み
 ========================= */
function openCsvPicker() {
  if (isFileDisabled.value) return
  fileInputRef.value?.click()
}

async function onCsvChange(e: Event) {
  csvErrors.value = []
  //アップロード中はボタンを非活性にする
  isSaving.value = true

  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  // CSV拡張子チェック
  if (!file.name.toLowerCase().endsWith('.csv')) {
    csvErrors.value = ['ファイル形式はCSVでお願いします。']
    input.value = ''
    //ボタンをアクティブに戻す
    isSaving.value = false
    return
  }

  try {
    // CSV登録（POST multipart）
    const res = await registerFlightRoutesByCsv(
      selectedAreaId.value!, // areaOk が true なので non-null
      file,
    )

    // APIが message_ids を返す仕様の場合
    if (res.message_ids?.length) {
      if (res.message_ids?.includes('loadfailed904')) {
        // CSVに問題がある場合のエラーは赤枠メッセージ
        csvErrors.value = res.message_ids.map((id) => `登録に失敗しました: ${id}`)
        isSaving.value = false
        return
      } else if (res.message_ids?.includes('noarea908')) {
        // エリアなしエラー、エリア選択も使用不可にする
        noAreaError.value = getFieldMessage(res.message_ids)
        isSaving.value = false
        return
      } else {
        // その他のエラー（航路上限数超過）
        maxCsvError.value = getFieldMessage(res.message_ids)
        isSaving.value = false
        return
      }
    }

    //アップロード後ボタン非活性を解除
    isSaving.value = false

    // 成功後：一覧再取得
    await fetchAndSetRoutes(selectedAreaId.value!)

    //登録完了トーストを表示
    show(getFieldMessage('operationncompleted004'))
  } finally {
    isSaving.value = false
    // 同じCSVを再選択できるようにする
    input.value = ''
  }
}

/** =========================
 * CSVテンプレダウンロード（public/flight_route_template.csv）
 ========================= */
function downloadCsvFormat() {
  const base = import.meta.env.BASE_URL
  const href = `${base}flight_route_template.csv`

  const a = document.createElement('a')
  a.href = href
  a.download = 'flight_route_template.csv'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

const go = async (action: () => unknown | Promise<unknown>, e?: Event) => {
  if (e?.currentTarget instanceof HTMLElement) {
    e.currentTarget.blur()
  }

  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur()
  }

  await action()
}

// iPhone Safari でメニューボタンをタップした場合、遷移先画面のボタンにフォーカスが当たってしまうため
// touchend 後に発生する click は無視
const handleTouchNavigate = (action: () => unknown | Promise<unknown>, e?: Event) => {
  lastTouchNavigateAt = Date.now()
  void go(action, e)
}
const handleClickNavigate = (action: () => unknown | Promise<unknown>, e?: Event) => {
  if (Date.now() - lastTouchNavigateAt < 800) {
    return
  }

  void go(action, e)
}

/** =========================
 * 航路情報設定画面に遷移
 ========================= */
function editRoute(routeId: number) {
  if (selectedAreaId.value == null) return

  return router.push({
    name: 'FlightRoutePointsPage',
    params: {
      areaId: selectedAreaId.value,
      routeId,
    },
  })
}

/** 設定トップへ */
function goToSettings() {
  return router.push('/settings')
}
</script>

<style scoped>
.link {
  font-size: 16px;
  margin-top: 10px;
  background: transparent;
  border: none;
  padding: 0;
  color: var(--blue02);
  text-decoration: underline;
  cursor: pointer;
  font-weight: 700;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.link:hover {
  opacity: 0.7;
}
</style>
